import { HowToAnswer } from "../components/interactions/HowToAnswer";
/**
 * WCC360 v2 — Part 1 for the 18+ band.
 *
 * ── CANON-PENDING · UNRATIFIED AMENDMENT TO X-STUDY-002 ──────────────────
 * `p1_reality_check` now offers a FIFTH option: "I'd do both — study and
 * earn at the same time", mapped to part-time study capacity. Yv approved
 * this as CANON-PENDING; it is NOT yet ratified in the Item Bank. It is
 * recorded here, at the option site below, in `types.ts` (both reality
 * maps) and in `copy.ts` for the 15–18 restem. Same convention as the
 * domain-gate change in the recommendation engine.
 * Rationale: someone who intends to study while earning previously had to
 * pick "I'd find a shorter route in" or "I'd start earning with them", and
 * both under-report their real study capacity.
 * ─────────────────────────────────────────────────────────────────────────
 *
 * Every screen here commits on an explicit Continue. Nothing advances on
 * selection alone — a learner should never have to guess whether a tap
 * registered.
 */
import { useState } from "react";
import { ChoiceScreen, type Choice } from "../components/ChoiceScreen";
import { useAssessmentV2 } from "../store";
import { useScreenCopy } from "../copy";

import type {
  Qualification, StudyAppetite, RealityCheck, SalaryBand, SalaryFlex, WorkLocation,
  CareerIntent,
} from "../types";
import { SALARY_BAND_LABEL } from "../types";
import { PROFESSIONAL_FIELDS, CAREER_INTENTS } from "../background";

interface ScreenProps { onNext: () => void }

export function QualificationScreen({ onNext }: ScreenProps) {
  const { state, setPart1 } = useAssessmentV2();
  const choices: readonly Choice<Qualification>[] = [
    { value: "school", label: "School (10th or 12th)" },
    { value: "diploma", label: "ITI, diploma or polytechnic", hint: "A skills or trade qualification after school" },
    { value: "bachelors", label: "Bachelor's degree" },
    { value: "masters", label: "Master's or professional qualification" },
  ] as const;
  return (
    <ChoiceScreen
      title="Highest qualification you hold"
      subtitle="Where you are today — not what you're aiming for."
      choices={choices}
      value={state.part1.qualification}
      onSelect={(v) => setPart1({ qualification: v })}
      onContinue={onNext}
    />
  );
}

export function StudyAppetiteScreen({ onNext }: ScreenProps) {
  const { state, setPart1 } = useAssessmentV2();
  const c = useScreenCopy("p1_study_appetite");
  // 15–18 restems the stem and all four labels. Same four ranks, same order,
  // same MIN rule against the reality check — only the wording moves.
  const choices = c.choices<StudyAppetite>([
    { value: "none", label: "None, I need to move now" },
    { value: "short_6mo", label: "A short course, up to 6 months" },
    { value: "part_time_1_2y", label: "1–2 years, part-time", hint: "Studying alongside a job" },
    { value: "full_degree", label: "A full degree if it's the right move" },
  ]);
  return (
    <ChoiceScreen
      title={c.t("title", "How much more study can you take on right now?")}
      subtitle={c.t("subtitle", "")}
      choices={choices}
      value={state.part1.study_appetite}
      onSelect={(v) => setPart1({ study_appetite: v })}
      onContinue={onNext}
    />
  );
}

export function RealityCheckScreen({ onNext }: ScreenProps) {
  const { state, setPart1 } = useAssessmentV2();
  const c = useScreenCopy("p1_reality_check");
  // The corroborator for X-STUDY-001. 15–18 drops "neither" (it needs a
  // money-or-time distinction nobody has yet) and reframes the pressure as
  // watching your friends start earning first.
  const choices = c.choices<RealityCheck>([
    { value: "would_sign_up", label: "I'd sign up" },
    { value: "want_numbers", label: "I'd want to know if it pays off first" },
    // ── CANON-PENDING · UNRATIFIED AMENDMENT TO X-STUDY-002 ──────────────
    // Fifth option, approved by Yv, not yet ratified in the Item Bank.
    // Maps to `part_time_1_2y` study capacity (see REALITY_TO_APPETITE and
    // REALITY_TO_APPETITE_15_18 in types.ts). Without it, someone who plans
    // to study while earning has to pick "shorter route in" or "start
    // earning" — both of which under-report their real study capacity.
    { value: "study_and_work", label: "I'd do both — study and earn at the same time" },
    // ─────────────────────────────────────────────────────────────────────
    { value: "cant_money", label: "I couldn't — money" },
    { value: "cant_time", label: "I couldn't — time" },
    { value: "cant_neither", label: "I couldn't — neither" },
  ]);
  return (
    <ChoiceScreen
      title={c.t("title", "The reality check")}
      subtitle={c.t(
        "subtitle",
        "The right career for you needs a two-year part-time course, starting in January. " +
        "Honestly, what happens?",
      )}
      choices={choices}
      value={state.part1.reality_check}
      onSelect={(v) => setPart1({ reality_check: v })}
      onContinue={onNext}
      ackFor={
        c.band === "15to18"
          ? {
              would_sign_up: "That's a real commitment. It opens things up.",
              want_numbers: "Fair. We'll show the numbers before you commit to anything.",
              study_and_work: "That's a real route, and more common than people think.",
              cant_time: "Good to know — we'll look hard at the shorter routes.",
              cant_money: "Understood. We'll show you what earning sooner looks like.",
            }
          : {
              would_sign_up: "Committed — that opens things up.",
              want_numbers: "Fair. We'll show the numbers before you commit to anything.",
              study_and_work: "Studying while you earn — we'll keep part-time routes in.",
              cant_money: "Understood. We'll stay inside what's doable now.",
              cant_time: "Understood. We'll stay inside what's doable now.",
              cant_neither: "Got it. Let's look at moves that don't need retraining.",
            }
      }
    />
  );
}


export function SalaryFloorScreen({ onNext }: ScreenProps) {
  const { state, setPart1 } = useAssessmentV2();
  const choices: readonly Choice<SalaryBand>[] = [
    { value: "2_5", label: SALARY_BAND_LABEL["2_5"], hint: "LPA = lakhs per year, before tax" },
    { value: "5_10", label: SALARY_BAND_LABEL["5_10"] },
    { value: "10_20", label: SALARY_BAND_LABEL["10_20"] },
    { value: "20_plus", label: SALARY_BAND_LABEL["20_plus"] },
  ] as const;
  return (
    <ChoiceScreen
      title="What do you need to earn for a move to be worth it?"
      subtitle="Total package, per year. We'll only show you work that clears this bar."
      choices={choices}
      value={state.part1.salary_floor}
      onSelect={(v) => setPart1({ salary_floor: v })}
      onContinue={onNext}
    />
  );
}

export function SalaryFlexScreen({ onNext }: ScreenProps) {
  const { state, setPart1 } = useAssessmentV2();
  // Option values and weights are untouched — only the stem is plainer.
  const choices: readonly Choice<SalaryFlex>[] = [
    { value: "none", label: "No — the number is the number" },
    { value: "small_10", label: "A little — around 10%" },
    { value: "meaningful_25", label: "Yes — 25% or more, for the right move" },
    { value: "not_driving", label: "Money isn't really what's driving this" },
  ] as const;
  return (
    <ChoiceScreen
      title="Would you be OK earning less if it meant doing work you love?"
      subtitle="Just for the first year, while you get in."
      choices={choices}
      value={state.part1.salary_flex}
      onSelect={(v) => setPart1({ salary_flex: v })}
      onContinue={onNext}
      ackFor={{
        none: "Straight answer — noted.",
        small_10: "Small room, big options.",
        meaningful_25: "That opens the door to work you'd otherwise miss.",
        not_driving: "Money matters, but not more than the work itself.",
      }}
    />
  );
}

/** Combined screen: years of experience + where you can work. */
export function ExperienceLocationScreen({ onNext }: ScreenProps) {
  const { state, setPart1 } = useAssessmentV2();
  const [years, setYears] = useState<number>(state.part1.years_experience ?? 0);
  const [loc, setLoc] = useState<WorkLocation | undefined>(state.part1.work_location);

  const canSubmit = loc !== undefined;

  const locChoices: { v: WorkLocation; label: string }[] = [
    { v: "anywhere", label: "Anywhere" },
    { v: "my_city", label: "My city only" },
    { v: "remote_only", label: "Remote only" },
  ];

  return (
    <div className="w-full max-w-2xl mx-auto">
      <h1 className="font-display text-3xl md:text-4xl font-medium leading-tight">
        A little about your working life so far
      </h1>
      <div className="mt-4"><HowToAnswer>Move the slider to your years of experience, then pick one place you'd work.</HowToAnswer></div>

      <div className="mt-10">
        <label className="block text-sm text-muted-foreground mb-3">
          Years of paid work experience — <span className="text-foreground font-medium">{years}{years >= 40 ? "+" : ""}</span>
        </label>
        <input
          type="range"
          min={0}
          max={40}
          step={1}
          value={years}
          onChange={(e) => setYears(Number(e.target.value))}
          className="w-full accent-primary h-2"
          aria-label="Years of experience"
        />
        <div className="mt-1 flex justify-between text-xs text-muted-foreground">
          <span>0</span><span>10</span><span>20</span><span>30</span><span>40+</span>
        </div>
      </div>

      <div className="mt-10">
        <div className="text-sm text-muted-foreground mb-3">Where can you work?</div>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
          {locChoices.map(({ v, label }) => {
            const selected = loc === v;
            return (
              <button
                key={v}
                onClick={() => setLoc(v)}
                aria-pressed={selected}
                className={`rounded-2xl border px-4 py-4 min-h-[56px] text-base transition-all
                  ${selected ? "border-primary bg-primary/10" : "border-border bg-card hover:border-foreground/30"}
                  focus:outline-none focus-visible:ring-2 focus-visible:ring-primary`}
              >
                {label}
              </button>
            );
          })}
        </div>
      </div>

      <div className="mt-12 flex justify-end">
        <button
          disabled={!canSubmit}
          onClick={() => {
            setPart1({ years_experience: years, work_location: loc });
            onNext();
          }}
          className="min-h-[48px] px-6 rounded-2xl bg-primary text-primary-foreground font-medium
            disabled:opacity-40 disabled:cursor-not-allowed hover:bg-primary/90
            focus:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 focus-visible:ring-offset-background"
        >
          Continue
        </button>
      </div>
    </div>
  );
}

/**
 * X-BACKGROUND-001 — professional history and background.
 *
 * Served only when the learner has already told us they have paid work
 * experience (see `isScreenSkipped`). Two answers: the field they've been
 * paid in, and where they want the next move to go. Both bounded, neither
 * ever an eligibility filter — and a field someone wants out of is never
 * boosted. See `background.ts`.
 *
 * CANON-PENDING: not yet ratified in the Item Bank.
 */
export function BackgroundScreen({ onNext }: ScreenProps) {
  const { state, setPart1 } = useAssessmentV2();
  const [field, setField] = useState<string | undefined>(state.part1.professional_field);
  const [intent, setIntent] = useState<CareerIntent | undefined>(state.part1.career_intent);

  const years = state.part1.years_experience ?? 0;
  const canSubmit = field !== undefined && intent !== undefined;

  return (
    <div className="w-full max-w-2xl mx-auto">
      <h1 className="font-display text-3xl md:text-4xl font-medium leading-tight">
        You've worked {years >= 40 ? "40+ years" : years === 1 ? "a year" : `${years} years`}. What in?
      </h1>
      <p className="mt-3 text-muted-foreground">
        We ask so your experience counts for something — not so we keep showing you the same work.
      </p>
      <div className="mt-4"><HowToAnswer>Pick one field and one goal, then tap Continue.</HowToAnswer></div>

      <fieldset className="mt-10">
        <legend className="text-sm text-muted-foreground mb-3">
          The field you've actually been paid in
        </legend>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          {PROFESSIONAL_FIELDS.map((f) => {
            const selected = field === f.id;
            return (
              <button
                key={f.id}
                type="button"
                onClick={() => setField(f.id)}
                aria-pressed={selected}
                className={`text-left rounded-2xl border px-4 py-4 min-h-[56px] transition-all
                  ${selected ? "border-primary bg-primary/10" : "border-border bg-card hover:border-foreground/30"}
                  focus:outline-none focus-visible:ring-2 focus-visible:ring-primary`}
              >
                <span className="block text-base">{f.label}</span>
                {f.hint && (
                  <span className="mt-1 block text-xs text-muted-foreground">{f.hint}</span>
                )}
              </button>
            );
          })}
        </div>
      </fieldset>

      <fieldset className="mt-12">
        <legend className="text-sm text-muted-foreground mb-3">
          And where do you want the next move to go?
        </legend>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          {CAREER_INTENTS.map((c) => {
            const selected = intent === c.value;
            return (
              <button
                key={c.value}
                type="button"
                onClick={() => setIntent(c.value)}
                aria-pressed={selected}
                className={`text-left rounded-2xl border px-4 py-4 min-h-[56px] transition-all
                  ${selected ? "border-primary bg-primary/10" : "border-border bg-card hover:border-foreground/30"}
                  focus:outline-none focus-visible:ring-2 focus-visible:ring-primary`}
              >
                <span className="block text-base">{c.label}</span>
                <span className="mt-1 block text-xs text-muted-foreground">{c.hint}</span>
              </button>
            );
          })}
        </div>
      </fieldset>

      {intent === "switch" && (
        <p className="mt-6 rounded-2xl border border-primary/40 bg-primary/5 p-4 text-sm text-muted-foreground">
          Good. We won't push more of the same at you — and we'll show you what your
          years already cover on the way in.
        </p>
      )}

      <div className="mt-12 flex justify-end">
        <button
          disabled={!canSubmit}
          onClick={() => {
            setPart1({ professional_field: field, career_intent: intent });
            onNext();
          }}
          className="min-h-[48px] px-6 rounded-2xl bg-primary text-primary-foreground font-medium
            disabled:opacity-40 disabled:cursor-not-allowed hover:bg-primary/90
            focus:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 focus-visible:ring-offset-background"
        >
          Continue
        </button>
      </div>
    </div>
  );
}
